/**
 * MODULE: platform/browser/frontend/components/BrowserSessionStatus.tsx
 * PURPOSE: Session connection indicator for the viewport toolbar and chat header.
 * DEPENDENCIES: browserSessionStatusLabel
 * PUBLIC_API: BrowserSessionStatus
 * SIDE_EFFECTS: none
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-17 [2.2.1407] — Action count suffix; data-testid for E2E.
 */

import type { BrowserSession } from '../types/browser.types';
import { isProxiedSteelLiveUrl } from '../utils/browserLiveViewUrl';
import { formatBrowserSessionStatusLabel } from '../utils/browserSessionStatusLabel';

interface BrowserSessionStatusProps {
  session: BrowserSession | null;
  isConnecting: boolean;
  sessionExpired?: boolean;
  actionCount?: number;
  provider?: 'steel' | 'browserbase' | 'unknown';
}

export function BrowserSessionStatus({
  session,
  isConnecting,
  sessionExpired = false,
  actionCount,
  provider,
}: BrowserSessionStatusProps) {
  const label = formatBrowserSessionStatusLabel({
    sessionExpired,
    hasSession: Boolean(session),
    isConnecting,
    actionCount,
    provider: provider ?? session?.provider,
    steelProxied: isProxiedSteelLiveUrl(session?.liveViewUrl ?? ''),
  });
  const dotClass = sessionExpired
    ? 'bg-amber-500'
    : session
      ? 'bg-green-500'
      : isConnecting
        ? 'bg-amber-400 animate-pulse'
        : 'bg-gray-400';

  return (
    <span
      className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400"
      data-testid="browser-session-status"
    >
      <span className={`h-2 w-2 rounded-full ${dotClass}`} aria-hidden />
      {label}
    </span>
  );
}
